import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback,useEffect, useMemo, useRef } from 'react';

import { OEM_REPORT_KEY_ENUM } from '@ekhodealer/ekho-common';
import { Button, Dropdown, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { formatNumberWithCommas } from '@common/lib/currency';
import { capitalizeFirstLetter } from '@common/lib/strings';

import { usePostReports } from '../../api/usePostReports';
import {
	isGeneratingPaymentReconciliationReportAtom,
	paymentReconciliationReportDataAtom,
	type PaymentReconciliationReportItem,
} from '../../atoms/paymentReconciliationAtoms';

import './styles.css';

const selectedMonthAtom = atom(String(new Date().getMonth() + 1)); // '1'-'12'
const selectedYearAtom = atom(String(new Date().getFullYear()));

const monthOptions = [
	{ id: '1', label: 'January' },
	{ id: '2', label: 'February' },
	{ id: '3', label: 'March' },
	{ id: '4', label: 'April' },
	{ id: '5', label: 'May' },
	{ id: '6', label: 'June' },
	{ id: '7', label: 'July' },
	{ id: '8', label: 'August' },
	{ id: '9', label: 'September' },
	{ id: '10', label: 'October' },
	{ id: '11', label: 'November' },
	{ id: '12', label: 'December' },
];

// Generate year options for current year and past 10 years
const getYearOptions = () => {
	const currentYear = new Date().getFullYear();
	const years = [];
	for (let i = 0; i <= 10; i++) {
		const year = currentYear - i;
		years.push({ id: String(year), label: year.toString() });
	}
	return years;
};

export default function ReportsPaymentReconciliationToolbar() {
	const [selectedMonth, setSelectedMonth] = useAtom(selectedMonthAtom);
	const [selectedYear, setSelectedYear] = useAtom(selectedYearAtom);
	const setPaymentReconciliationReportData = useSetAtom(paymentReconciliationReportDataAtom);
	const setIsGenerating = useSetAtom(isGeneratingPaymentReconciliationReportAtom);
	const isGenerating = useAtomValue(isGeneratingPaymentReconciliationReportAtom);
	const reportData = useAtomValue(paymentReconciliationReportDataAtom);
	const { mutateAsync: generateReportAsync } = usePostReports(
		OEM_REPORT_KEY_ENUM.OUTGOING_PAYMENTS,
	);

	const yearOptions = useMemo(() => getYearOptions(), []);

	const hasReportData = useMemo(() => {
		return reportData !== null && reportData.length > 0;
	}, [reportData]);

	const handleDownloadReport = () => {
		if (!reportData || reportData.length === 0) return;

		// Define CSV headers - matching table column order exactly
		const headers = [
			'Customer',
			'Transfer date',
			'Transfer amount',
			'Stripe fees',
			'Ekho fee',
			'Transfer platform',
			'Receipt type',
			'Vehicle ID',
			'Transfer ID',
			'Recipient name',
			'Recipient ID',
			'Receipt status',
			'Transfer status',
			'Receipt ID',
			'Placed order ID',
			'Formatted VIN',
		];

		// Convert report data to CSV rows
		const rows = reportData.map((item) => {
			const transferAmount = parseFloat(item.transferAmount || '0');
			const processingFees = parseFloat(item.processingFees || '0');
			const ekhoFee = parseFloat(item.ekhoFee || '0');

			// Use transferDate to match the UI table display
			const transferDateValue = item.transferDate
				? typeof item.transferDate === 'string'
					? parseInt(item.transferDate, 10)
					: item.transferDate
				: null;
			const date = transferDateValue ? new Date(transferDateValue) : null;
			const formattedDate = date
				? date.toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
				  })
				: '--';

			return [
				item.customer || 'Unknown',
				formattedDate,
				formatNumberWithCommas(transferAmount.toFixed(2)),
				formatNumberWithCommas(processingFees.toFixed(2)),
				formatNumberWithCommas(ekhoFee.toFixed(2)),
				item.transferPlatform || 'N/A',
				item.receiptType || 'N/A',
				item.uid || 'N/A',
				item.transferId || 'N/A',
				item.recipientName || 'N/A',
				item.recipientId || 'N/A',
				capitalizeFirstLetter(item.receiptStatus || 'N/A'),
				capitalizeFirstLetter(item.transferStatus || 'N/A'),
				item.receiptId,
				item.placedOrderId || 'N/A',
				item.formattedVin || 'N/A',
			];
		});

		// Combine headers and rows
		const csvContent = [headers, ...rows]
			.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
			.join('\n');

		// Create BOM for UTF-8 (helps Excel open it correctly)
		const BOM = '\uFEFF';
		const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

		// Generate filename with current date
		const now = new Date();
		const month = String(now.getMonth() + 1).padStart(2, '0');
		const day = String(now.getDate()).padStart(2, '0');
		const year = now.getFullYear();
		const filename = `Ekho_Payment_Reconciliation_Report_${month}_${day}_${year}.csv`;

		// Create download link and trigger download
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', filename);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const hasGeneratedOnLoad = useRef(false);
	// Incremented on every generate call; used to discard stale responses
	const generationIdRef = useRef(0);

	const handleGenerateReport = useCallback(() => {
		// Calculate start and end dates for the selected month
		const year = parseInt(selectedYear, 10);
		const month = parseInt(selectedMonth, 10);
		const startDate = new Date(year, month - 1, 1);
		const endDate = new Date(year, month, 0, 23, 59, 59, 999); // Last day of month

		// Send timestamps (numbers) as strings, which the backend will parseInt
		const reportPeriodStart = String(startDate.getTime());
		const reportPeriodEnd = String(endDate.getTime());

		// Increment generation ID so any in-flight response from a previous call is ignored
		generationIdRef.current += 1;
		const currentGenerationId = generationIdRef.current;

		// Clear previous results immediately so the skeleton shows and stale rows never bleed through
		setPaymentReconciliationReportData(null);
		setIsGenerating(true);

		const params = {
			reportKey: OEM_REPORT_KEY_ENUM.OUTGOING_PAYMENTS,
			reportPeriodStart,
			reportPeriodEnd,
		};

		// Use mutateAsync to ensure we can handle the promise directly
		// reportKey is already provided to the hook, so we don't need to pass it again
		generateReportAsync(params)
			.then((data) => {
				// Discard this response if a newer generation has already been started
				if (currentGenerationId !== generationIdRef.current) return;

				setIsGenerating(false);
				// Type assertion: hook overload ensures PaymentReconciliationReportItem[] when OUTGOING_PAYMENTS is passed
				const dataToSet = (Array.isArray(data)
					? data
					: data ?? []) as unknown as PaymentReconciliationReportItem[];
				setPaymentReconciliationReportData(dataToSet);
			})
			.catch((error) => {
				if (currentGenerationId !== generationIdRef.current) return;
				console.error('Toolbar mutateAsync .catch():', error);
				setIsGenerating(false);
			});
	}, [
		selectedYear,
		selectedMonth,
		setIsGenerating,
		generateReportAsync,
		setPaymentReconciliationReportData,
	]);

	// Auto-generate report on component mount with current month/year
	useEffect(() => {
		if (!hasGeneratedOnLoad.current && reportData === null) {
			hasGeneratedOnLoad.current = true;
			handleGenerateReport();
		}
	}, [reportData, handleGenerateReport]);

	return (
		<Group itemsAlignX='space-between' itemsAlignY='end'>
			<Group itemsSpacing={8} itemsAlignY='center' noWrap>
				<Stack>
					<Text size='footnote' color='secondary'>
						Month
					</Text>
					<Dropdown
						onSelectionChange={(value) => setSelectedMonth(value as string)}
						options={monthOptions}
						className='dropdown'
						value={selectedMonth}
						size='small'
					/>
				</Stack>
				<Stack>
					<Text size='footnote' color='secondary'>
						Year
					</Text>
					<Dropdown
						onSelectionChange={(value) => setSelectedYear(value as string)}
						options={yearOptions}
						className='dropdown'
						value={selectedYear}
						size='small'
					/>
				</Stack>
				<Stack>
					<div style={{ height: '16px' }} />
					<Button
						variant='filled'
						size='small'
						onPress={handleGenerateReport}
						isDisabled={isGenerating}
						>
						{isGenerating ? 'Generating...' : 'Generate'}
					</Button>
				</Stack>
			</Group>

			<Button
				variant='secondary'
				size='small'
				onPress={handleDownloadReport}
				isDisabled={!hasReportData}>
				Download
			</Button>
		</Group>
	);
}
